import translate from '@translate';
import React, { FC, useMemo } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import Select, { OptionType } from 'src/components/Select';
import TextField from 'src/components/TextField';
import Switch from 'src/components/Switch';
import Alert from '@mui/material/Alert';
import { EgonDeliveryType, EgonRouteType } from 'src/types/egonRoute';
import { FormErrorsType } from 'src/types/global';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import {
    filterEgonServiceCodes,
    getEgonCountryOptions,
    getEgonDeliveryLabel,
    getEgonDeliveryOptions,
} from 'src/utils/egonRoutes';
import { countryOptionsCodes } from 'src/utils/countryOptions';

export interface DetailPropsType {
    editData: EgonRouteType;
    editErrors: FormErrorsType;
    newRoute: boolean;
    disableEdit: boolean;
    shipperCodes: ConstantsCodeType[];
    serviceCodes: ConstantsCodeType[];
    deliveries: EgonDeliveryType[] | null;
    deliveriesFailed: boolean;
    deliveriesLoading: boolean;
    onChangeShipper: (value: string | undefined) => void;
    onChangeService: (value: string | undefined) => void;
    onChangeCountries: (value: string[]) => void;
    onChangeCountry: (value: string | undefined) => void;
    onChangeDelivery: (deliveryId: number | undefined) => void;
    onChangeField: (key: 'ENABLED' | 'NOTE', value: number | string | null) => void;
}

const Detail: FC<DetailPropsType> = (props) => {
    const { editData } = props;

    const serviceOptions: OptionType[] = useMemo(
        () => filterEgonServiceCodes(props.serviceCodes, editData.SHIPPER_CODE),
        [props.serviceCodes, editData.SHIPPER_CODE]
    );
    const countryOptions: OptionType[] = useMemo(() => {
        const options = getEgonCountryOptions(props.serviceCodes, editData.SERVICE_CODE_VALUE, countryOptionsCodes);
        // keep the saved country visible even if the service no longer lists it
        if (!props.newRoute && editData.COUNTRY_CODE && !options.find((o) => o.value === editData.COUNTRY_CODE)) {
            options.push({ value: editData.COUNTRY_CODE, label: editData.COUNTRY_CODE });
        }
        return options;
    }, [props.serviceCodes, editData.SERVICE_CODE_VALUE, editData.COUNTRY_CODE, props.newRoute]);

    const deliveryOptions: OptionType[] = useMemo(() => {
        const options = getEgonDeliveryOptions(props.deliveries || []);
        // keep the saved delivery visible even if it is not offered anymore
        if (editData.EGON_DELIVERY_ID && !options.find((o) => o.value === editData.EGON_DELIVERY_ID)) {
            options.unshift({
                value: editData.EGON_DELIVERY_ID,
                label: getEgonDeliveryLabel({ id: editData.EGON_DELIVERY_ID, name: editData.EGON_DELIVERY_NAME }),
            });
        }
        return options;
    }, [props.deliveries, editData.EGON_DELIVERY_ID, editData.EGON_DELIVERY_NAME]);

    return (
        <Grid>
            <GridItem cols={3}>
                <div data-testid="egon-route-shipper">
                    <Select
                        value={editData.SHIPPER_CODE}
                        onChange={(value) => props.onChangeShipper((value as string) || undefined)}
                        name="SHIPPER_CODE"
                        label={translate('egonRouteDetail.shipper')}
                        options={props.shipperCodes}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                    />
                </div>
            </GridItem>
            <GridItem cols={3}>
                <div data-testid="egon-route-service">
                    <Select
                        value={editData.SERVICE_CODE_VALUE}
                        onChange={(value) => props.onChangeService((value as string) || undefined)}
                        name="SERVICE_CODE_VALUE"
                        label={translate('egonRouteDetail.service')}
                        options={serviceOptions}
                        error={props.editErrors.SERVICE_CODE_VALUE}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                    />
                </div>
            </GridItem>
            <GridItem cols={6}>
                <div data-testid="egon-route-country">
                    {props.newRoute ? (
                        <Select
                            value={editData.COUNTRY_CODES || []}
                            onChange={(value) => props.onChangeCountries((value as string[]) || [])}
                            name="COUNTRY_CODES"
                            label={translate('egonRouteDetail.countries')}
                            options={countryOptions}
                            error={props.editErrors.COUNTRY_CODES}
                            multiple
                            fullWidth
                            disabled={props.disableEdit}
                        />
                    ) : (
                        <Select
                            value={editData.COUNTRY_CODE}
                            onChange={(value) => props.onChangeCountry((value as string) || undefined)}
                            name="COUNTRY_CODE"
                            label={translate('egonRouteDetail.country')}
                            options={countryOptions}
                            error={props.editErrors.COUNTRY_CODE}
                            fullWidth
                            required
                            disabled={props.disableEdit}
                        />
                    )}
                </div>
            </GridItem>
            {props.deliveriesFailed && (
                <GridItem cols={12}>
                    <Alert severity="warning" data-testid="egon-route-deliveries-warning">
                        {translate('egonRouteDetail.deliveriesFailed')}
                    </Alert>
                </GridItem>
            )}
            <GridItem cols={6}>
                <div data-testid="egon-route-delivery">
                    {props.deliveriesFailed ? (
                        <TextField
                            type="number"
                            value={editData.EGON_DELIVERY_ID ?? ''}
                            error={props.editErrors.EGON_DELIVERY_ID}
                            onChange={(value) => props.onChangeDelivery(value === '' || value === undefined ? undefined : Number(value))}
                            name="EGON_DELIVERY_ID"
                            label={translate('egonRouteDetail.deliveryId')}
                            fullWidth
                            required
                            min={1}
                            step="1"
                            disabled={props.disableEdit}
                        />
                    ) : (
                        <Select
                            value={editData.EGON_DELIVERY_ID}
                            onChange={(value) => props.onChangeDelivery(value ? Number(value) : undefined)}
                            name="EGON_DELIVERY_ID"
                            label={translate('egonRouteDetail.delivery')}
                            options={deliveryOptions}
                            error={props.editErrors.EGON_DELIVERY_ID}
                            loading={props.deliveriesLoading}
                            fullWidth
                            required
                            disabled={props.disableEdit}
                        />
                    )}
                </div>
            </GridItem>
            <GridItem cols={3}>
                <TextField
                    value={editData.EGON_TRANSFER_TYPE ?? ''}
                    name="EGON_TRANSFER_TYPE"
                    label={translate('egonRouteDetail.transferType')}
                    fullWidth
                    disabled
                />
            </GridItem>
            <GridItem cols={3}>
                <div data-testid="egon-route-enabled">
                    <Switch
                        value={!!editData.ENABLED}
                        onChange={(checked) => props.onChangeField('ENABLED', checked ? 1 : 0)}
                        label={translate('egonRouteDetail.enabled')}
                        disabled={props.disableEdit}
                    />
                </div>
            </GridItem>
            <GridItem cols={12}>
                <TextField
                    value={editData.NOTE ?? ''}
                    onChange={(value) => props.onChangeField('NOTE', (value as string) || null)}
                    name="NOTE"
                    label={translate('egonRouteDetail.note')}
                    fullWidth
                    multiline
                    maxLength={500}
                    disabled={props.disableEdit}
                />
            </GridItem>
        </Grid>
    );
};

export default Detail;
